import type { BioanalysisPhaseOne } from "./bioanalysisPhaseOne";

export type SampleEvidenceStatus = "supported" | "planned" | "gap";
export type SampleRiskSeverity = "critical" | "major";

export interface SampleConditionItem {
  id: string;
  label: string;
  value: string;
  basis: string;
  sourceUrl: string;
  status: SampleEvidenceStatus;
}

export interface SampleWorkflowItem {
  id: string;
  stage: string;
  controlPoint: string;
  currentEvidence: string;
  protocolDecision: string;
  requiredRecord: string;
  status: SampleEvidenceStatus;
}

export interface StabilityCoverageItem {
  id: string;
  study: string;
  scenario: string;
  currentEvidence: string;
  protocolDecision: string;
  sourceTitle: string;
  sourceUrl: string;
  status: SampleEvidenceStatus;
}

export interface SampleRiskItem {
  id: string;
  risk: string;
  impact: string;
  preventiveControl: string;
  deviationRecord: string;
  severity: SampleRiskSeverity;
}

export interface BioanalysisPhaseTwo {
  readiness: "ready" | "partial" | "insufficient";
  score: number;
  summary: string;
  conditions: SampleConditionItem[];
  workflow: SampleWorkflowItem[];
  stability: StabilityCoverageItem[];
  risks: SampleRiskItem[];
  gaps: string[];
  limitations: string[];
}

export interface BuildBioanalysisPhaseTwoInput {
  phaseOne: BioanalysisPhaseOne;
  data?: Record<string, unknown>;
}

const FDA_M10_URL =
  "https://www.fda.gov/regulatory-information/search-fda-guidance-documents/m10-bioanalytical-method-validation-and-study-sample-analysis";
const EMA_M10_URL =
  "https://www.ema.europa.eu/en/ich-m10-bioanalytical-method-validation-scientific-guideline";
const ICH_M10_FAQ_URL =
  "https://database.ich.org/sites/default/files/M10_Guideline_FAQs_2022_0616_0.pdf";

interface ArticleEvidence {
  title: string;
  url: string;
  searchable: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object" && !Array.isArray(item))
    : [];
}

function text(value: unknown): string {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const normalized = value.trim();
    const key = normalized.toLocaleLowerCase();
    if (!normalized || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function collectPubmedEvidence(data?: Record<string, unknown>): ArticleEvidence[] {
  const pubmed = asRecord(data?.pubmed);
  const candidates = [...asRecordArray(pubmed.articles)];
  asRecordArray(pubmed.evidence_categories).forEach((category) => {
    candidates.push(...asRecordArray(category.articles));
  });

  const seen = new Set<string>();
  return candidates.flatMap((article) => {
    const title = text(article.title);
    const abstract = text(article.abstract);
    const searchable = [
      title,
      abstract,
      Array.isArray(article.tags) ? article.tags.map(text).join(" ") : text(article.tags),
      Array.isArray(article.matched_terms) ? article.matched_terms.map(text).join(" ") : text(article.matched_terms),
    ].filter(Boolean).join(" ");
    const key = text(article.pmid) || text(article.doi).toLocaleLowerCase() || title.toLocaleLowerCase();
    if (!key || !searchable || seen.has(key)) return [];
    seen.add(key);
    return [{
      title: title || "未命名文献",
      url: text(article.url) || (text(article.pmid) ? `https://pubmed.ncbi.nlm.nih.gov/${text(article.pmid)}/` : ""),
      searchable,
    }];
  });
}

function compactEvidence(value: string, maxLength = 180): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1).trim()}…` : normalized;
}

function evidenceSentence(articles: ArticleEvidence[], pattern: RegExp): { value: string; url: string } {
  for (const article of articles) {
    const sentences = article.searchable.split(/(?<=[.!?。；;])\s+/);
    const sentence = sentences.find((item) => pattern.test(item));
    pattern.lastIndex = 0;
    if (sentence) return { value: compactEvidence(sentence), url: article.url };

    const match = article.searchable.match(pattern);
    pattern.lastIndex = 0;
    if (match?.[0]) return { value: compactEvidence(match[0]), url: article.url };
  }
  return { value: "", url: "" };
}

function explicitValue(articles: ArticleEvidence[], patterns: RegExp[]): { value: string; url: string } {
  for (const article of articles) {
    for (const pattern of patterns) {
      const match = article.searchable.match(pattern);
      if (match?.[1]) return { value: compactEvidence(match[1], 120), url: article.url };
    }
  }
  return { value: "", url: "" };
}

function firstMethodValue(phaseOne: BioanalysisPhaseOne, selector: (item: BioanalysisPhaseOne["methodEvidence"][number]) => string): { value: string; url: string } {
  for (const item of phaseOne.methodEvidence) {
    const value = selector(item).trim();
    if (value) return { value, url: item.url || item.doiUrl || item.fullTextUrl };
  }
  return { value: "", url: "" };
}

function buildConditions(phaseOne: BioanalysisPhaseOne, articles: ArticleEvidence[]): SampleConditionItem[] {
  const analytes = unique(phaseOne.analytes.map((item) => item.nameEn || item.nameCn).filter(Boolean));
  const matrices = unique(phaseOne.methodEvidence.flatMap((item) => item.matrix));
  const platforms = unique(phaseOne.methodEvidence.map((item) => item.platform).filter(Boolean));
  const preparations = unique(phaseOne.methodEvidence.flatMap((item) => item.preparation));
  const volume = firstMethodValue(phaseOne, (item) => item.sampleVolume);
  const tube = explicitValue(articles, [
    /\b((?:K[23][- ]?)?EDTA(?: plasma| tube)?|lithium heparin(?: plasma| tube)?|sodium heparin(?: plasma| tube)?|sodium citrate(?: plasma| tube)?)\b/i,
  ]);
  const centrifugation = evidenceSentence(articles, /(?:centrifug(?:e|ed|ation)|\d[\d,]*\s*[×x]?\s*g\b|\d[\d,]*\s*rpm\b)/i);
  const processWindow = evidenceSentence(articles, /(?:within\s+\d+(?:\.\d+)?\s*(?:min(?:ute)?s?|h(?:ours?)?)|processed|separated)[^.;]{0,80}(?:collection|sampling|centrifug|plasma|serum)/i);
  const storage = evidenceSentence(articles, /(?:stored|storage|kept|frozen)[^.;]{0,100}(?:[-−–]\s*\d+\s*°?\s*C|dry ice|liquid nitrogen)/i);
  const transport = evidenceSentence(articles, /(?:transport(?:ed|ation)?|ship(?:ped|ment)?|dry ice|temperature logger)/i);

  const condition = (
    id: string,
    label: string,
    value: string,
    basis: string,
    sourceUrl: string,
    missingIsGap = true,
  ): SampleConditionItem => ({
    id,
    label,
    value: value || "待方案/实验室手册确认",
    basis: value ? basis : "当前结构化结果未提供该条件，不能按常见经验值代填。",
    sourceUrl: value ? sourceUrl : "",
    status: value ? "supported" : (missingIsGap ? "gap" : "planned"),
  });

  return [
    condition("analytes", "分析物", analytes.join("、"), "药品身份与复方活性成分识别结果", "", true),
    condition("matrix", "生物基质", matrices.join("、"), "PubMed 方法文献明确报告", phaseOne.methodEvidence.find((item) => item.matrix.length > 0)?.url || ""),
    condition("platform", "检测平台", platforms.join("、"), "PubMed 方法文献明确报告", phaseOne.methodEvidence.find((item) => item.platform)?.url || "", false),
    condition("preparation", "样本前处理", preparations.join("、"), "PubMed 方法文献明确报告", phaseOne.methodEvidence.find((item) => item.preparation.length > 0)?.url || "", false),
    condition("tube", "采血管 / 抗凝剂", tube.value, "PubMed 摘要或元数据明确报告", tube.url),
    condition("volume", "单次分析样本体积", volume.value, "PubMed 方法文献明确报告", volume.url, false),
    condition("centrifugation", "离心 / 分离条件", centrifugation.value, "PubMed 摘要明确报告", centrifugation.url),
    condition("process_window", "采集后处理时限", processWindow.value, "PubMed 摘要明确报告", processWindow.url),
    condition("storage", "储存条件", storage.value, "PubMed 摘要明确报告", storage.url),
    condition("transport", "运输条件", transport.value, "PubMed 摘要明确报告", transport.url),
  ];
}

function conditionValue(conditions: SampleConditionItem[], id: string): string {
  const item = conditions.find((condition) => condition.id === id);
  return item?.status === "supported" ? item.value : "未从当前证据确认";
}

function buildWorkflow(conditions: SampleConditionItem[]): SampleWorkflowItem[] {
  const supported = (id: string) => conditions.find((item) => item.id === id)?.status === "supported";
  const evidence = (id: string) => conditionValue(conditions, id);
  return [
    {
      id: "pre_collection",
      stage: "采集前",
      controlPoint: "受试者、访视、时间点和样本标签预先对应",
      currentEvidence: "属于研究文件控制项，不由药品检索结果直接给出。",
      protocolDecision: "在方案、采样表和实验室手册统一样本编号、计划时间点及允许时间窗。",
      requiredRecord: "样本标签模板、采样时间表、人员培训记录",
      status: "planned",
    },
    {
      id: "collection_tube",
      stage: "样本采集",
      controlPoint: "基质、采血管 / 抗凝剂与采集量",
      currentEvidence: `基质：${evidence("matrix")}；管型：${evidence("tube")}；分析体积：${evidence("volume")}`,
      protocolDecision: "锁定管型、最小采集量、混匀方式和实际采样时间记录规则。",
      requiredRecord: "采样记录、管型批号、实际采样时间、异常说明",
      status: supported("matrix") && supported("tube") ? "supported" : "gap",
    },
    {
      id: "processing",
      stage: "离心与分离",
      controlPoint: "采集后等待时间、离心力 / 转速、温度、时长及分离完成时间",
      currentEvidence: `处理时限：${evidence("process_window")}；离心条件：${evidence("centrifugation")}`,
      protocolDecision: "以方法开发和稳定性数据锁定最大处理时限及离心参数，并规定超窗处置。",
      requiredRecord: "离心设备编号、开始/结束时间、温度、偏差记录",
      status: supported("process_window") && supported("centrifugation") ? "supported" : "gap",
    },
    {
      id: "aliquot",
      stage: "分装",
      controlPoint: "分装体积、管数、容器材质及冻融用途分配",
      currentEvidence: `现有单次分析体积：${evidence("volume")}`,
      protocolDecision: "结合复测、ISR 和备份需求确定分装管数，避免不必要的重复冻融。",
      requiredRecord: "分装体积、分装管数量、容器批号、样本位置",
      status: supported("volume") ? "supported" : "planned",
    },
    {
      id: "temporary_storage",
      stage: "现场暂存",
      controlPoint: "分离后至冷冻、出库前的温度和最长允许时间",
      currentEvidence: `现有储存证据：${evidence("storage")}`,
      protocolDecision: "用短期稳定性覆盖现场暂存条件；连续记录入冻时间与温度偏差。",
      requiredRecord: "入冻时间、设备温度、报警与偏差处理记录",
      status: supported("storage") ? "supported" : "gap",
    },
    {
      id: "transport",
      stage: "运输",
      controlPoint: "包装验证、运输温度、温度监测、交接与到达判定",
      currentEvidence: `现有运输证据：${evidence("transport")}`,
      protocolDecision: "运输条件必须被稳定性覆盖；定义温度偏差判定、接收隔离和放行流程。",
      requiredRecord: "交接链、温度记录仪数据、运输单、接收检查表",
      status: supported("transport") ? "supported" : "gap",
    },
    {
      id: "receipt_storage",
      stage: "实验室接收与长期储存",
      controlPoint: "样本清点、状态核对、储存位置和长期稳定性覆盖",
      currentEvidence: `现有储存证据：${evidence("storage")}`,
      protocolDecision: "定义接收拒收标准、样本追踪、备用冰箱及稳定性到期预警。",
      requiredRecord: "接收清单、LIMS 位置、温度曲线、稳定性到期日",
      status: supported("storage") ? "supported" : "gap",
    },
    {
      id: "analysis",
      stage: "分析与复测",
      controlPoint: "批次安排、样本复测、稀释、ISR 与结果可追溯性",
      currentEvidence: "ICH M10 要求项，具体规则需写入生物分析计划和 SOP。",
      protocolDecision: "分析前定义批接受、复测原因代码、ISR 选择和盲态保持要求。",
      requiredRecord: "分析批记录、复测审计轨迹、ISR 清单、结果锁定记录",
      status: "planned",
    },
    {
      id: "retention_disposal",
      stage: "留样与销毁",
      controlPoint: "留样期限、剩余样本用途、授权销毁与隐私保护",
      currentEvidence: "属于方案、知情同意和样本管理制度共同控制项。",
      protocolDecision: "确认留样期限、二次使用授权及销毁审批链。",
      requiredRecord: "留样清单、授权文件、销毁审批和见证记录",
      status: "planned",
    },
  ];
}

function buildStability(articles: ArticleEvidence[]): StabilityCoverageItem[] {
  const definitions = [
    {
      id: "bench_top",
      study: "短期 / 台面稳定性",
      scenario: "覆盖采集、离心分离、分装和分析前等待的实际最长时间与温度。",
      pattern: /(?:bench[- ]?top|short[- ]term|room temperature|ambient temperature)\s+stabil/i,
      decision: "验证条件不得短于临床现场和实验室实际暴露上限。",
    },
    {
      id: "freeze_thaw",
      study: "冻融稳定性",
      scenario: "覆盖首次分析、可能复测、稀释或 ISR 导致的最大冻融次数。",
      pattern: /freeze[-– ]?thaw(?:\s+stabil|\s+cycles?)/i,
      decision: "按计划最大冻融次数验证，并在样本层面记录实际冻融次数。",
    },
    {
      id: "long_term",
      study: "长期稳定性",
      scenario: "覆盖首次采样至最后一批样本完成分析的最长储存期。",
      pattern: /long[- ]term\s+stabil/i,
      decision: "长期稳定性时长必须覆盖实际样本储存时长，并设置到期预警。",
    },
    {
      id: "processed",
      study: "处理后 / 自动进样器稳定性",
      scenario: "覆盖提取完成后在自动进样器内等待、重进样和批次中断情形。",
      pattern: /(?:processed|post[- ]preparative|autosampler|reinjection)\s+stabil/i,
      decision: "验证温度和时长应覆盖最长分析批、停机及允许重进样情形。",
    },
    {
      id: "whole_blood",
      study: "全血稳定性（适用时）",
      scenario: "当采集后不能立即分离，或存在细胞分配风险时评估全血阶段稳定性。",
      pattern: /whole blood\s+stabil/i,
      decision: "结合化合物性质和处理时限决定是否开展，并覆盖最坏处理延迟。",
    },
    {
      id: "stock_solution",
      study: "储备液 / 工作液稳定性",
      scenario: "覆盖标准品、内标和工作液的配制、储存与使用周期。",
      pattern: /(?:stock|working) solution\s+stabil/i,
      decision: "分别定义储备液、工作液和内标溶液的条件、时长与复核要求。",
    },
  ];

  return definitions.map((definition) => {
    const evidence = evidenceSentence(articles, definition.pattern);
    return {
      id: definition.id,
      study: definition.study,
      scenario: definition.scenario,
      currentEvidence: evidence.value || "当前摘要层证据未明确报告该稳定性结果。",
      protocolDecision: definition.decision,
      sourceTitle: definition.id === "stock_solution" ? "ICH M10 Questions and Answers" : "ICH M10 Bioanalytical Method Validation",
      sourceUrl: definition.id === "stock_solution" ? ICH_M10_FAQ_URL : (definition.id === "whole_blood" ? EMA_M10_URL : FDA_M10_URL),
      status: evidence.value ? "supported" as const : "gap" as const,
    };
  });
}

const SAMPLE_RISKS: SampleRiskItem[] = [
  {
    id: "time_deviation",
    risk: "采样时间偏离计划时间点",
    impact: "可能改变浓度-时间曲线并影响 Cmax、AUC、tmax 等参数解释。",
    preventiveControl: "预设允许时间窗，记录实际采样时间，以实际时间进入 PK 数据集。",
    deviationRecord: "计划/实际时间、偏差时长、原因、PK 分析处理决定",
    severity: "critical",
  },
  {
    id: "wrong_tube",
    risk: "采血管、抗凝剂或基质不符合要求",
    impact: "可能产生凝血、吸附、干扰或基质不一致，影响定量可靠性。",
    preventiveControl: "颜色与文字双重标识，采样前核对，接收时复核管型和样本状态。",
    deviationRecord: "实际管型、样本状态、隔离位置、方法负责人评估",
    severity: "critical",
  },
  {
    id: "processing_delay",
    risk: "离心、分离或入冻延迟",
    impact: "可能发生降解、细胞分配变化或代谢物转化。",
    preventiveControl: "使用时间戳和计时提醒，按稳定性覆盖的最大时限设置报警。",
    deviationRecord: "各步骤时间戳、实际温度、超限时长、稳定性覆盖判断",
    severity: "critical",
  },
  {
    id: "temperature_excursion",
    risk: "暂存、运输或长期储存温度超限",
    impact: "样本完整性和浓度结果可能无法由验证数据支持。",
    preventiveControl: "连续温度监测、备用设备、温度偏差隔离和科学评估。",
    deviationRecord: "最高/最低温度、持续时间、样本清单、稳定性桥接依据",
    severity: "critical",
  },
  {
    id: "sample_quality",
    risk: "溶血、高脂、样本不足或容器破损",
    impact: "可能引起基质效应、无法复测或样本丢失。",
    preventiveControl: "接收时按统一标准检查并记录；方法验证覆盖适用的特殊基质。",
    deviationRecord: "样本外观、可用体积、缺陷代码、分析/剔除决定",
    severity: "major",
  },
  {
    id: "chain_of_custody",
    risk: "标签不一致、交接链中断或位置不可追溯",
    impact: "可能导致样本错配，属于数据完整性重大风险。",
    preventiveControl: "唯一标识、双人或系统核对、LIMS 审计轨迹和位置扫描。",
    deviationRecord: "发现时间、涉及样本、交接人员、纠正与根因调查",
    severity: "critical",
  },
];

export function buildBioanalysisPhaseTwo(input: BuildBioanalysisPhaseTwoInput): BioanalysisPhaseTwo {
  const articles = collectPubmedEvidence(input.data);
  const conditions = buildConditions(input.phaseOne, articles);
  const workflow = buildWorkflow(conditions);
  const stability = buildStability(articles);
  const supportedConditions = conditions.filter((item) => item.status === "supported").length;
  const supportedStability = stability.filter((item) => item.status === "supported").length;
  const criticalConditionIds = ["analytes", "matrix", "tube", "centrifugation", "process_window", "storage"];
  const criticalSupported = criticalConditionIds.filter((id) => conditions.find((item) => item.id === id)?.status === "supported").length;
  const score = Math.min(100, Math.round(
    (supportedConditions / conditions.length) * 45
    + (supportedStability / stability.length) * 35
    + 20,
  ));
  const readiness: BioanalysisPhaseTwo["readiness"] = criticalSupported >= 5 && supportedStability >= 4
    ? "ready"
    : criticalSupported >= 2 || supportedStability >= 1
      ? "partial"
      : "insufficient";

  const gaps = unique([
    ...conditions
      .filter((item) => item.status === "gap")
      .map((item) => `需在方案、实验室手册或方法验证资料中确认：${item.label}。`),
    ...stability
      .filter((item) => item.status === "gap")
      .map((item) => `需用研究实际条件覆盖：${item.study}。`),
  ]);
  const summary = readiness === "ready"
    ? `已从当前证据确认 ${supportedConditions} 项样本条件，并识别 ${supportedStability} 类稳定性证据；可形成样本管理草案，仍需用实验室原始验证报告最终锁定。`
    : readiness === "partial"
      ? `已获得部分样本处理证据，但采集、运输或稳定性条件尚未完整覆盖；当前结果适合用于编制问题清单，不宜直接作为实验室执行参数。`
      : "已建立样本采集至分析的全流程核查框架，但当前检索证据不足以形成可执行的样本处理条件。";

  return {
    readiness,
    score,
    summary,
    conditions,
    workflow,
    stability,
    risks: SAMPLE_RISKS.map((item) => ({ ...item })),
    gaps,
    limitations: [
      "仅把当前结构化结果和 PubMed 题名/摘要中明确出现的条件标记为已有证据。",
      "样本采集、处理、运输和储存参数必须由研究方案、实验室手册及经批准的方法验证资料共同确认。",
      "稳定性条件应覆盖研究实际最坏情形；文献证据不能替代本实验室的方法验证和样本管理记录。",
    ],
  };
}
